import React, { useState, useEffect, useRef, useMemo } from 'react';
import Icon from './Icon';
import { formatListDate } from '../lib/dateFormat';
import { LOCAL_INDEX_DAYS } from '../lib/localIndex';
import { ARROW_SKIM_MS } from '../lib/bodyCache';
import { LABEL_COLOURS } from '../lib/labels';
import { senderAvatar } from '../lib/senderAvatar';
import { isV2 } from '../lib/uiVersion';
import styles from './MessageList.module.css';

// /V2 (decided once per tab — see lib/uiVersion.js): rows carry a sender
// avatar.
const V2 = isV2();

// Outlook-style date bucket label for a message row. Buckets:
//   Today · Yesterday · Earlier this week · Last week · Earlier this
//   month · <Month YYYY>
//
// Week boundaries are Monday-Sunday (UK convention). Previously
// "Last week" was implemented as "the 6 days before yesterday" — so
// an email from Wednesday showed under Last week even when today
// was Friday of the same calendar week. Users (rightly) expect Last
// week to mean the PREVIOUS calendar week, with the gap between
// Yesterday and Last week falling under "Earlier this week".
//
// Worked example, today = Friday 5 Jun 2026:
//   Today                = 5 Jun
//   Yesterday            = 4 Jun (Thu)
//   Earlier this week    = 1-3 Jun (Mon, Tue, Wed)
//   Last week            = 25-31 May (Mon-Sun)
//   Earlier this month   = the rest of Jun not covered above (empty here)
//   May 2026 / earlier   = labelled by month name
// en-GB long month names — a constant instead of toLocaleDateString() per row.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Builds the bucket labeller for one render: the "now"-relative boundaries are
// worked out once, not once per row (this runs for every visible row).
function makeBucketLabeler() {
  const now = new Date();
  const startOfDay = (x) => { const c = new Date(x); c.setHours(0,0,0,0); return c.getTime(); };
  const today     = startOfDay(now);
  const yesterday = today - 86400000;
  // Monday of the current week. JS's getDay() returns 0 (Sun) → 6 (Sat);
  // (dow + 6) % 7 maps that to 0 (Mon) → 6 (Sun). Subtract that many
  // days from today's start-of-day to reach this Monday.
  const dow = new Date(today).getDay();
  const daysSinceMonday = (dow + 6) % 7;
  const startOfThisWeek = today - daysSinceMonday * 86400000;
  const startOfLastWeek = startOfThisWeek - 7 * 86400000;
  return function bucketLabelForDate(dateStr) {
    if (!dateStr) return 'Older';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Older';
    const ts = d.getTime();
    if (ts >= today)           return 'Today';
    if (ts >= yesterday)       return 'Yesterday';
    if (ts >= startOfThisWeek) return 'Earlier this week';
    if (ts >= startOfLastWeek) return 'Last week';
    // Same calendar month? Group as "Earlier this month".
    if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) {
      return 'Earlier this month';
    }
    // Otherwise label by month, e.g. "April 2026". Same year omits the year.
    const monthLabel = MONTHS[d.getMonth()];
    if (d.getFullYear() === now.getFullYear()) return monthLabel;
    return `${monthLabel} ${d.getFullYear()}`;
  };
}

// A stable function that always calls the LATEST `fn` — so memoised rows can
// keep the same callback prop across renders without ever calling a handler
// that closed over stale App state. Null stays null (rows test for presence).
function useStableHandler(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  const [stable] = useState(() => (...args) => (ref.current ? ref.current(...args) : undefined));
  return fn ? stable : null;
}

// Map of OwnerReactionType MAPI strings to the glyph the badge renders.
// Mirrors EMOJI_TO_REACTION in ReadingPane.js — kept in sync by hand
// because the two files share no module.
const REACTION_GLYPHS = {
  thumbsUp:  '👍',
  heart:     '❤️',
  celebrate: '🎉',
  laugh:     '😂',
  surprised: '😮',
  sad:       '😢',
};

// Ribbon-style flag icon used in the flag slot. Prototype uses a custom one
// slightly thicker than Icon.js's default flag.
function FlagRibbon({ size = 13, filled = false }) {
  const strokeWidth = filled ? 0 : 1.6;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 22V3" />
      <path d="M5 3h13.5a.5.5 0 0 1 .4.8l-3.1 4 3.1 4a.5.5 0 0 1-.4.8H5" />
    </svg>
  );
}

// Strip "URGENT" (with any decoration / position) from a subject so
// the word isn't shown next to the red URGENT pill — they'd be
// duplicating each other. The pill's visibility is decided by the
// caller via `\burgent\b` against the raw subject; this function
// handles every form the strip needs to cover:
//
//   "URGENT: foo"           → "foo"
//   "URGENT! foo"           → "foo"
//   "URGENT - foo"          → "foo"
//   "Re: URGENT: foo"       → "Re: foo"
//   "FW: URGENT - foo"      → "FW: foo"
//   "[URGENT] foo"          → "foo"
//   "(URGENT) foo"          → "foo"
//   "***URGENT*** foo"      → "foo"
//   "Q4 URGENT report"      → "Q4 report"     (URGENT mid-subject)
//   "foo URGENT"            → "foo"           (URGENT trailing)
//   "URGENT"                → ""              (caller substitutes "(no subject)")
//
// Earlier versions of this function only handled URGENT at the start
// of the subject (after Re:/Fw: prefixes) with strict trailing
// punctuation — bracketed and mid-subject variants slipped through,
// so the pill + word ended up side by side which the user reported
// as the fix being "back".
function stripUrgentMarker(subject) {
  return (subject || '')
    // 1) bracketed / asterisked variants — strip the brackets too
    .replace(/[\[\(\*]+\s*urgent\s*[\]\)\*]+[\s!:.\-]*/gi, ' ')
    // 2) bare URGENT (with word-boundary check so "urgentcare" survives)
    //    + any trailing punctuation that was attached to it
    .replace(/\burgent\b[\s!:.\-]*/gi, ' ')
    // 3) tidy: collapse internal whitespace runs
    .replace(/\s{2,}/g, ' ')
    // 4) tidy: strip leading punctuation left over after removing
    //    URGENT at the start (e.g. "URGENT: foo" → ": foo" before this)
    .replace(/^\s*[:\-]+\s*/, '')
    .trim();
}

// Column defaults / bounds. Label column deferred (backend not ready) so the
// grid is: [unread-dot] [from] [reply-indicator] [subject-flex] [date] [flag].
// date: wide enough for DD/MM/YYYY ("30/10/2026" needs ~68 px at the
// classic 11.5 px, more when bold/unread); was 58/42 for DD/MM/YY.
const COL_DEFAULTS = { from: 140, reply: 18, date: 78 };
const COL_MIN      = { from: 80,  reply: 0,  date: 74 };
const COL_MAX      = { from: 320, reply: 18, date: 120 };

function describeRecipients(msg) {
  const recipients = msg.toAddresses || [];
  if (!recipients.length) return msg.to || '(no recipient)';
  const first = recipients[0].name || recipients[0].email || '';
  return recipients.length > 1
    ? `${first} +${recipients.length - 1}`
    : first;
}

// Single source of truth for "is this attachment a noise file we should
// hide from the UI?" — exported so ReadingPane can use the same rule.
// Hits:
//   - .txt named like "log", "trace", "bounce", "scan fragment" etc.
//   - Microsoft Defender / ATP scan fragments: filenames look like
//     `00000000@2E251A20.F961EF69.txt` (8 zeros, @, hex, dot, hex,
//     `.txt`). The user's screenshot showed an inbox where every
//     attachment was one of these and they completely buried the
//     real attachment.
//   - Header-stripped originals: `ATT00001.txt` / `header.txt` etc.
export function isNoiseAttachment(att) {
  const name = String(att?.name || '');
  if (!name) return false;
  const base = name.replace(/\.[a-z0-9]{1,8}$/i, '');
  const hasOnlyHexAndPunct = /^[0-9A-F@._\-]+$/i.test(base);
  const hasContent         = /[0-9A-F]/i.test(base);
  const hasNonHexLetter    = /[G-Z]/i.test(base);

  // Strong MAPI / TNEF noise signature — overrides any extension.
  // Microsoft 365 unpacks Outlook's winmail.dat into individual
  // attachments with names like `FB0E2D65@EAC5632B.9F78066A00C30D6E`,
  // sometimes carrying a .png/.jpg extension that would otherwise
  // short-circuit past as "real content" via the REAL_CONTENT_EXT gate
  // below. The `@` is the distinctive marker: real user-named files
  // essentially never contain `@`, and the Dropbox / SaaS UUID-named
  // photos that gate was added for (`1d383eea-c299-…`) don't carry
  // `@` either. So a hex-only basename with `@` is noise regardless
  // of what extension it claims.
  if (hasOnlyHexAndPunct && hasContent && !hasNonHexLetter && base.includes('@')) {
    return true;
  }

  // Real-content extensions are never noise, even if the basename
  // happens to be a UUID / hex-only string. Dropbox-style auto-named
  // photos (1d383eea-c299-4b3d-a69c-42d18066db50.jpg) and SaaS
  // export filenames trip the hex-fragment heuristic below otherwise.
  // Restrict the heuristic to "no extension" / .txt / .log so genuine
  // attachments always render.
  const REAL_CONTENT_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|heic|tiff?|pdf|ai|eps|psd|doc[xm]?|xls[xm]?|ppt[xm]?|csv|zip|rar|7z|mp[34]|mov|avi|mkv|wmv|wav|aac|ogg|flac|eml|msg|odt|ods|odp|rtf|key|pages|numbers|json|xml|html?|svg|dwg|dxf|stl|step|stp|iges|igs)$/i;
  if (REAL_CONTENT_EXT.test(name)) return false;

  // Hex-only filename test for the no-extension / .txt cases.
  // Recent user report: filenames like `00000000@1B7EA878.5638EF6900000000`
  // come through with NO extension at all. The previous .txt-only
  // gate let those through. Now we strip any trailing extension (or
  // none) and check the remaining string.
  //
  // The match: hex digits + `@` + `.` + `-` + `_` only, AND must
  // contain at least one digit / hex letter, AND must NOT contain
  // any letter outside A–F. That's a tight fingerprint for Defender
  // ATP fragments / Outlook quarantine dumps / antimalware-scan
  // shrapnel — none of which a human would ever choose to attach.
  // Real filenames ("Order_2024.txt", "Quote-March.pdf") fail the
  // "no letter outside A–F" test on the first vowel.
  // Require either an `@` or a length suggesting a hex blob — a bare
  // "000000" wouldn't trip this, but the typical Defender pattern of
  // "8hex@8hex.16hex" definitely does.
  const hasFragmentMarker  = base.includes('@') || base.length >= 16;
  if (hasOnlyHexAndPunct && hasContent && !hasNonHexLetter && hasFragmentMarker) {
    return true;
  }
  // From here down: txt-only matchers, kept for the older noise patterns.
  if (!/\.txt$/i.test(name)) return false;
  // Generic ATTNNNNN.txt headers.
  if (/^att\d+\.txt$/i.test(name)) return true;
  if (/^header(s)?\.txt$/i.test(name)) return true;
  // Keyword-based fallback for older log-like naming.
  if (/\b(log|trace|bounce|sender|delivery|atpscan|atp-scan|scan|fragment|content|mailheader|original\s*message|safelinks|smimeMicrosoft|protect)\b/i.test(name)) return true;
  return false;
}

// Hide the row paperclip when EVERY attachment is noise. If at least
// one looks substantive we still want the user to see a clip on the
// row.
function looksLikeTxtLogAttachments(msg) {
  if (!msg?.hasAttachments) return false;
  const list = msg.attachments || [];
  // Row data may not yet include the attachment list (only the full
  // GET on click brings it in). When we can't see names, leave the
  // paperclip visible — better to over-show than hide useful files.
  if (!list.length) return false;
  return list.every(a => isNoiseAttachment(a));
}

// Section 6 #3: content-visibility "windowing" — ON by default (soaked clean).
// Lets the browser skip layout/paint of OFF-screen rows while keeping every row
// in the DOM, so keyboard nav, selection, sticky date-bucket headers and
// scroll-into-view all keep working; degrades gracefully where content-
// visibility is unsupported. Kill switch if it ever misbehaves for a specific
// browser/user (no redeploy needed):
//   localStorage.setItem('pp_render_cv','0'); location.reload();
// 44px is a rough per-row placeholder for the off-screen scrollbar estimate;
// it self-corrects as rows render.
const RENDER_CV = (() => {
  try { return localStorage.getItem('pp_render_cv') !== '0'; } catch { return true; }
})();
const CV_STYLE = { contentVisibility: 'auto', containIntrinsicSize: '0 44px' };
// With the list sorted by date the windowing is per DAY, not per row: one
// content-visibility box around each day's rows. Per row, Chrome tracked every
// row's visibility and painted each as its own isolated box on every scroll
// frame — with 700 rows loaded that was most of a frame on a slower laptop
// (perf/traceScroll.js), while switching it off made appends and the first
// paint pay for laying out every row. A day group keeps the skip and costs a
// few dozen boxes. Keyed by the day, so new mail joining today, or an older
// page appending further days, never moves a row to another group (no
// remounts). `auto` keeps a group's real height once it has been drawn; 44px
// a row is only the guess for groups never yet on screen.
function cvDayStyle(rowCount) {
  return { contentVisibility: 'auto', containIntrinsicSize: `auto 0px auto ${rowCount * 44}px` };
}
function localDayKey(dateStr) {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? 'none' : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// Memoised: a selection change, poll tick or page append re-renders only the
// rows whose props actually changed (the old and new active row), not every
// loaded row. Every callback prop must therefore be stable — MessageList
// passes them through useStableHandler — and per-row actions take msg.id
// rather than arriving as per-row closures.
const MessageRow = React.memo(function MessageRow({
  msg, active, onClick, onContext, onFlag, onPrefetch = null,
  widths, showTo, mailbox = '', mixedFolders = false,
  onSwipeUnread, onSwipeDelete,
  // Build Order — when active, the row gets a checkbox column and a
  // light-orange tint when ticked. tickedEmails is the active order's
  // map; presence of msg.id means ticked. tickedName is the user-typed
  // custom label that replaces the subject in the row.
  buildOrderActive = false,
  tickedEntry = null,                // { name, tickedAt } or null
  onToggleBuildOrderTick = null,     // (messageId, defaultName?) — ticks + names, or unticks
  ownCv = RENDER_CV,                 // false when the row sits in a day group (cvDayStyle)
  // (rename is done in the Build Order drawer's pencil, not from the list)
}) {
  // CSS custom property feeds the desktop grid; mobile media query
  // overrides with grid-template-areas. This avoids inline-style
  // overriding the @media-block rules.
  // Columns: dot · attach · from · reply · subject(flex) · date · flag.
  // The `attach` column is 18 px and houses the paperclip when an
  // email has file attachments — gives every row's contact name a
  // common left edge, which the eye reads as "tidier" instead of the
  // jagged old layout where the paperclip pushed names rightward by
  // ~14 px on rows with attachments only.
  const gridTemplate = `14px 18px ${widths.from}px ${widths.reply}px 1fr ${widths.date}px 22px`;
  const state = msg.completed ? 'completed' : msg.flagged ? 'flagged' : 'none';
  // Direction determination — only meaningful when the list mixes
  // sent + received (search, customer view, conversation thread).
  // `mailbox` is the logged-in user's email; `msg.from` is the
  // sender. If they match, this row is a Sent item.
  const isSent = !!mailbox && !!msg.from && String(msg.from).toLowerCase() === mailbox.toLowerCase();
  // showTo (folder-driven) wins; mixedFolders falls back to per-row
  // detection so search results clearly differentiate the two
  // directions in a single list.
  const directionToRecipients = showTo || (mixedFolders && isSent);
  const senderLabel = directionToRecipients
    ? describeRecipients(msg)
    : (msg.fromName || msg.from || '');

  // ── Touch state ─────────────────────────────────────────────────────
  // - Long-press (≥ 500 ms, no horizontal motion) → context menu.
  // - Horizontal swipe → mark unread (right) or delete (left).
  // - Vertical drag → list scrolls (handled by browser via touch-action: pan-y).
  // - Plain tap → row click (onClick fires when there's no swipe).
  const rowRef       = useRef(null);
  const pressTimer   = useRef(null);
  const startPos     = useRef({ x: 0, y: 0 });
  const axisLock     = useRef(null);   // null | 'h' | 'v'
  const swipeX       = useRef(0);
  const swipingHorz  = useRef(false);
  const SWIPE_TRIGGER = 80;            // px past which the action fires

  function setRowTransform(x, animated = false) {
    const el = rowRef.current;
    if (!el) return;
    el.style.transition = animated ? 'transform 200ms ease' : 'none';
    el.style.transform  = x ? `translateX(${x}px)` : '';
    // Its own compositor layer only while it is being swiped (see .row).
    el.style.willChange = x ? 'transform' : '';
    // Tell the wrapper which action is being revealed so the right
    // background colour shows behind the moving row.
    if (el.parentElement) {
      el.parentElement.dataset.swipe =
        x > 12 ? 'unread' : x < -12 ? 'delete' : 'none';
    }
  }

  function onTouchStart(e) {
    const t = e.touches[0];
    startPos.current = { x: t.clientX, y: t.clientY };
    axisLock.current = null;
    swipeX.current   = 0;
    swipingHorz.current = false;
    setRowTransform(0, false);

    pressTimer.current = setTimeout(() => {
      if (swipingHorz.current) return;
      onContext({ preventDefault() {}, clientX: startPos.current.x, clientY: startPos.current.y }, msg);
    }, 500);
  }

  function onTouchMove(e) {
    const t = e.touches[0];
    const dx = t.clientX - startPos.current.x;
    const dy = t.clientY - startPos.current.y;

    // Lock the gesture axis once we've moved ≥ 8 px in some direction.
    if (!axisLock.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisLock.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }

    if (axisLock.current === 'h') {
      // Horizontal swipe — translate the row, cancel any pending long-press.
      if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
      swipingHorz.current = true;
      swipeX.current = dx;
      setRowTransform(dx, false);
    } else {
      // Vertical scroll — let the browser handle it; cancel long-press
      // since the user is scrolling past the row, not pressing it.
      if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
    }
  }

  function onTouchEnd() {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
    if (axisLock.current !== 'h') {
      // Plain tap or vertical scroll — nothing to clean up.
      return;
    }
    const x = swipeX.current;
    if (x > SWIPE_TRIGGER) {
      // Right swipe → mark unread. Slide row off-screen, then trigger.
      const w = rowRef.current?.offsetWidth || 320;
      setRowTransform(w, true);
      setTimeout(() => {
        setRowTransform(0, false);
        onSwipeUnread && onSwipeUnread(msg.id);
      }, 180);
    } else if (x < -SWIPE_TRIGGER) {
      // Left swipe → delete.
      const w = rowRef.current?.offsetWidth || 320;
      setRowTransform(-w, true);
      setTimeout(() => {
        setRowTransform(0, false);
        onSwipeDelete && onSwipeDelete(msg.id);
      }, 180);
    } else {
      // Below threshold — snap back.
      setRowTransform(0, true);
    }
    swipingHorz.current = false;
    axisLock.current = null;
  }

  // If the user lifted their finger but a swipe was in progress, we treat
  // the click as cancelled (don't open the message). This prevents
  // accidental "swipe + immediate click" from selecting a row that's
  // about to be deleted.
  function handleClick() {
    if (swipingHorz.current) return;
    onClick(msg);
  }

  // URGENT pill only fires when the SUBJECT explicitly contains the
  // word "urgent" (case-insensitive, whole word). The Graph
  // `importance: 'high'` flag is no longer a trigger — too many
  // routine senders mark mail as high-importance by default, which
  // polluted the inbox with red URGENT pills on emails the user
  // never considered urgent. Multiple prior fixes adjusted the strip
  // regex but left this trigger in place; removing it is the actual
  // fix. (If we ever want a separate "Important" indicator for
  // sender-set high importance, it should be a different visual,
  // not the red URGENT pill.)
  const isUrgent = /\burgent\b/i.test(msg.subject || '');
  const subjectDisplay = isUrgent ? stripUrgentMarker(msg.subject) || '(no subject)' : (msg.subject || '(no subject)');

  return (
    <div
      className={styles.swipeWrap}
      data-swipe="none"
      style={ownCv ? CV_STYLE : undefined}
    >
      {/* Left action: revealed when swiping right (mark unread). */}
      <span className={`${styles.swipeAction} ${styles.swipeActionUnread}`} aria-hidden>
        <Icon name="envelopeClosed" size={16} /> <span>Unread</span>
      </span>
      {/* Right action: revealed when swiping left (delete). */}
      <span className={`${styles.swipeAction} ${styles.swipeActionDelete}`} aria-hidden>
        <span>Delete</span> <Icon name="trashThin" size={16} />
      </span>

      <div
        ref={rowRef}
        className={`${styles.row} ${active ? styles.rowActive : ''} ${msg.isRead ? styles.rowRead : styles.rowUnread} ${msg.completed ? styles.rowCompleted : ''} ${msg.flagged && !msg.completed ? styles.rowFlagged : ''}`}
        style={{
          '--rowCols': gridTemplate,
          // Build Order tick — light-orange tint when ticked. No size
          // change, just background. (#fff7ed = Tailwind orange-50.)
          ...(tickedEntry ? { background: '#fff7ed' } : null),
        }}
        data-msg-id={msg.id}
        onMouseDown={(e) => {
          // Start fetching the body on the press so it is (usually) there by
          // the time the click lands. Primary button on the row itself — not
          // the flag or the Build Order tick, which do not open the email.
          if (e.button !== 0 || !onPrefetch || e.target.closest?.('[data-row-action]')) return;
          onPrefetch(msg);
        }}
        onClick={handleClick}
        onContextMenu={(e) => onContext(e, msg)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        draggable
        onDragStart={(e) => {
          // Internal drag (desktop): ComposeModal listens for this type and
          // fetches the MIME to attach as .eml. Also set text/plain so drops
          // into other targets (email-client search boxes etc.) get something
          // sensible. Mobile doesn't fire dragstart; touchhandlers run
          // instead.
          e.dataTransfer.setData('text/x-onpoint-message', JSON.stringify({ id: msg.id, subject: msg.subject || '' }));
          e.dataTransfer.setData('text/plain', msg.subject || '');
          // External drop (drag to OS desktop / file manager): Chrome /
          // Edge / Firefox honour the DownloadURL meta-format and fetch
          // the URL when dropped, saving as the chosen filename. The
          // backend's /download endpoint serves the message MIME with a
          // proper Content-Disposition: attachment header.
          const safe = (msg.subject || 'email').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 80) + '.eml';
          const fullUrl = window.location.origin
            + `/api/mail/messages/${encodeURIComponent(msg.id)}/download`;
          try {
            e.dataTransfer.setData(
              'DownloadURL',
              `message/rfc822:${safe}:${fullUrl}`
            );
          } catch { /* not supported in some browsers — internal drop still works */ }
          e.dataTransfer.effectAllowed = 'copyMove';
        }}
      >
        {buildOrderActive ? (
          // Build Order checkbox replaces the unread-dot column when
          // a Build Order tab is active. Tick → prompts for an email
          // name. Click on the checkbox itself doesn't select the row
          // (stopPropagation); click on the rest of the row still
          // opens the email as normal.
          // Build Order checkbox replaces the unread-dot column when
          // a Build Order tab is active. Take the unreadDot's GRID slot
          // but override its 7×7 px / orange-circle sizing — a real
          // checkbox needs ~14 px to render properly and the orange
          // background would otherwise show through.
          //
          // Click on the box → tick + prompt for name. Click on the
          // rest of the row still opens the email (stopPropagation
          // on the box only).
          <div
            data-row-action
            className={styles.unreadDot}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 14, height: 14,
              background: 'transparent',
              borderRadius: 0,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (tickedEntry) {
                onToggleBuildOrderTick && onToggleBuildOrderTick(msg.id);
                return;
              }
              // No rename prompt on tick — just add it. Renaming is done in
              // the Build Order drawer (pencil) if needed.
              const defaultName = (msg.subject || 'Email').slice(0, 60);
              onToggleBuildOrderTick && onToggleBuildOrderTick(msg.id, defaultName);
            }}
            title={tickedEntry ? 'Untick' : 'Tick for Build Order'}
          >
            <input
              type="checkbox"
              checked={!!tickedEntry}
              readOnly
              style={{ margin: 0, accentColor: '#f97316', cursor: 'pointer', width: 14, height: 14 }}
              tabIndex={-1}
              aria-label={tickedEntry ? `Ticked as ${tickedEntry.name}` : 'Tick for Build Order'}
            />
          </div>
        ) : (
          <div className={styles.unreadDot} />
        )}
        {/* Dedicated paperclip column — fixed width so contact names
            in every row share a left edge whether or not the row has
            attachments. Suppressed when the only files are txt-log
            noise (Defender ATP / postmaster bounces). */}
        <div className={styles.attachCol} aria-hidden>
          {msg.hasAttachments && !looksLikeTxtLogAttachments(msg) && (
            <span className={styles.attachIcon} title="Has attachments">
              <Icon name="paperclip" size={12} />
            </span>
          )}
        </div>
        <div className={styles.from}>
          {/* Sent-vs-Received marker — only renders when the list
              mixes both directions (search, customer view). A single
              "↗ Sent" pill on outgoing rows gives senior-frontend-grade
              clarity at a glance: green-tinted accent for sent so the
              eye scans them without reading the email column. Inbound
              rows get nothing extra — folder context makes them
              implicitly "received". */}
          {mixedFolders && isSent && (
            <span className={styles.sentPill} title="You sent this">
              <Icon name="sent" size={10} stroke={2} />
              Sent
            </span>
          )}
          {V2 && (() => {
            const av = senderAvatar(senderLabel);
            return <span className={styles.avatar} style={av.style} aria-hidden="true">{av.initials}</span>;
          })()}
          <span className={styles.fromText}>
            {/* Lead "To:" / "From:" prefix when mixed — matches Outlook's
                conversation thread convention so a sent reply is never
                confused with the recipient's own email. */}
            {mixedFolders && (
              <span className={styles.directionLabel}>
                {directionToRecipients ? 'To: ' : 'From: '}
              </span>
            )}
            {senderLabel}
          </span>
          {/* On mobile, the label appears inline next to the sender as
              plain text (" — Quote"). Desktop hides this and keeps the
              boxed pill in .labelSlot below. */}
          {msg.label && (
            <span
              className={styles.labelInline}
              style={{ color: LABEL_COLOURS[msg.label] || 'var(--text-3)' }}
            > — {msg.label}</span>
          )}
        </div>
        <div className={styles.replyCol}>
          {msg.replied   && <span className={styles.repliedIcon}   title="Replied"><Icon name="reply"   size={12} stroke={2} /></span>}
          {msg.forwarded && <span className={styles.repliedIcon} title="Forwarded"><Icon name="forward" size={12} stroke={2} /></span>}
        </div>
        <div className={styles.subjectLine}>
          {/* URGENT pill removed by request — keeps the subject row
              visually quiet. The text is still stripped from the
              subject display via stripUrgentMarker so a manually
              prefixed "URGENT" doesn't double up either. */}
          {tickedEntry ? (
            // Ticked-for-Build-Order: show the user's chosen name (semibold
            // orange) followed by the original subject as a muted tail.
            // Renaming is done in the Build Order drawer (pencil), so no
            // click-to-rename prompt here.
            <span
              className={styles.subject}
              title={`Ticked for Build Order. Original subject: ${subjectDisplay}`}
            >
              <span style={{ color: '#c2410c', fontWeight: 600 }}>🏷 {tickedEntry.name || subjectDisplay}</span>
              <span style={{ color: '#9ca3af', marginLeft: 6 }}>· {subjectDisplay}</span>
            </span>
          ) : (
            <span className={styles.subject}>{subjectDisplay}</span>
          )}
          {msg.reactions?.count > 0 && (
            <span
              className={styles.reactionBadge}
              title={
                msg.reactions.ownerReactionType
                  ? `${msg.reactions.count} reaction${msg.reactions.count === 1 ? '' : 's'} · you reacted ${REACTION_GLYPHS[msg.reactions.ownerReactionType] || ''}`.trim()
                  : `${msg.reactions.count} reaction${msg.reactions.count === 1 ? '' : 's'}`
              }
            >
              {REACTION_GLYPHS[msg.reactions.ownerReactionType] || '👍'} {msg.reactions.count}
            </span>
          )}
          <span className={styles.labelSlot}>
            {msg.label && (
              <span
                className={styles.labelPill}
                style={{ color: LABEL_COLOURS[msg.label] || '#666', borderColor: LABEL_COLOURS[msg.label] || '#ccc' }}
                title={`Label: ${msg.label}`}
              >
                {msg.label}
              </span>
            )}
          </span>
        </div>
        <span className={styles.time}>{formatListDate(msg.date)}</span>
        <span
          data-row-action
          className={`${styles.flagSlot} ${styles[`flagSlot_${state}`]}`}
          onClick={(e) => { e.stopPropagation(); onFlag && onFlag(msg.id); }}
          title={state === 'completed' ? 'Completed — click to clear' : state === 'flagged' ? 'Flagged — click to mark complete' : 'Flag'}
        >
          {state === 'completed'
            ? <Icon name="check" size={14} stroke={2.4} />
            : state === 'flagged'
              ? <FlagRibbon size={13} filled />
              : <span className={styles.flagHover}><FlagRibbon size={13} /></span>}
        </span>
      </div>
    </div>
  );
});

const FILTER_OPTS = [
  { key: 'all',     label: 'All' },
  { key: 'unread',  label: 'Unread' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'files',   label: 'Has files' },
  { key: 'mentionsMe', label: 'Mentions me' },
];
const SORT_FIELDS = [
  { key: 'date',    label: 'Date' },
  { key: 'from',    label: 'From' },
  { key: 'subject', label: 'Subject' },
];

export default function MessageList({
  messages,
  loading,
  selectedId,
  folderName,
  showTo = false,
  // True when the rendered list mixes Sent + Received items in a way
  // the user can't infer from folder context — search results and
  // customer-thread views are the canonical examples. Each row then
  // self-determines direction off `msg.from === mailbox` and renders
  // a "↗ Sent" pill + "To: …" prefix on outbound rows.
  mixedFolders = false,
  onSelect,
  onContext,
  onFlag,
  // (msg) on a row press, ahead of the click — App prefetches the body.
  onPrefetch = null,
  onDelete = null,
  // Mobile-only: row-level swipe gestures. Right-swipe → mark unread,
  // left-swipe → delete. Wired to the same handlers App.js uses for the
  // context-menu "Mark as unread" / "Delete" actions, so swipe is just
  // a fast-path UI for what already exists.
  onSwipeUnread = null,
  onSwipeDelete = null,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  // Footer button: when the active search came from the local (LOCAL_INDEX_DAYS)
  // FTS index, render a "Search older mail" button under the list.
  // Click triggers a Graph search for the same query and appends.
  // Hidden when the active result source already includes Graph.
  onSearchOlder = null,
  searchingOlder = false,
  todayCount: serverTodayCount,
  mailbox = '',
  labelFilter = null,
  onEmptyFolder = null,
  // Set by App.js when the user is viewing the Drafts folder — fires
  // the orphan-drafts review modal. Renders a "Sweep orphans" pill in
  // the header next to Empty Folder when set. Null for non-Drafts
  // folders (no pill rendered).
  onSweepOrphanDrafts = null,
  // Filter state is OWNED by App.js — we pipe it through to the server
  // so Unread/Flagged/With-files span the whole folder, not just what's
  // currently paginated in. MessageList just renders and fires onChange.
  filter = 'all',
  onFilterChange = () => {},
  // Suppresses the scroll-near-bottom auto-load of the next page. App.js
  // sets this during search so the result list doesn't re-shuffle under
  // the user's cursor as they're trying to click a row. The "Load more
  // messages" button at the end of the list still works — pagination
  // becomes explicit while a query is active.
  disableAutoLoad = false,
  // Optional banner rendered immediately below the folder header and
  // above the first row. Used by App.js's thread-mode UI to surface a
  // "Thread · domain · N emails · Clear" strip without forking a new
  // component. Pass null to omit.
  headerSlot = null,
  // Build Order — when a Build Order tab is active, MessageList shows
  // a checkbox per row + light-orange background on ticked rows + the
  // user-chosen name (set on first tick) in place of the subject.
  // tickedEmails: { [messageId]: { name, tickedAt } } — read from
  // the active order's state in App.js. Null/empty = no Build Order
  // active → checkboxes hidden, normal row rendering.
  buildOrderActive = false,
  buildOrderTickedEmails = {},
  onToggleBuildOrderTick = null,   // (messageId, defaultName?) → toggles
  onRenameBuildOrderTick = null,   // (messageId, newName) → updates name
}) {
  const listRef = useRef(null);
  // Stable per-row callbacks (see MessageRow): App hands down fresh functions
  // every render, which would defeat the row memo.
  const rowOnSelect               = useStableHandler(onSelect);
  const rowOnContext              = useStableHandler(onContext);
  const rowOnFlag                 = useStableHandler(onFlag);
  const rowOnPrefetch             = useStableHandler(onPrefetch);
  const rowOnSwipeUnread          = useStableHandler(onSwipeUnread);
  const rowOnSwipeDelete          = useStableHandler(onSwipeDelete);
  const rowOnToggleBuildOrderTick = useStableHandler(onToggleBuildOrderTick);
  const [sortBy,   setSortBy]   = useState(() => localStorage.getItem('pp_sort_by') || 'date');
  const [sortDir,  setSortDir]  = useState(() => localStorage.getItem('pp_sort_dir') || 'desc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen,   setSortOpen]   = useState(false);
  useEffect(() => { localStorage.setItem('pp_sort_by',  sortBy); },  [sortBy]);
  useEffect(() => { localStorage.setItem('pp_sort_dir', sortDir); }, [sortDir]);

  // Close dropdowns on any outside click.
  useEffect(() => {
    if (!filterOpen && !sortOpen) return undefined;
    const close = () => { setFilterOpen(false); setSortOpen(false); };
    const id = setTimeout(() => window.addEventListener('click', close), 0);
    return () => { clearTimeout(id); window.removeEventListener('click', close); };
  }, [filterOpen, sortOpen]);
  // Auto-load the next page when the user scrolls within 200 px of the bottom.
  // Skipped when `disableAutoLoad` is set (App.js does this during search,
  // so a result list doesn't re-shuffle under the user's cursor as they're
  // about to click a row — the "Load more messages" button at the end of
  // the list stays functional).
  //
  // "Near" is two screens ahead, not the last 200 px: a page of older mail
  // takes a round trip (~150 ms) plus a render, and a normal flick covers
  // 200 px in under 100 ms — so the old trigger fired too late and the person
  // sat at the bottom watching "Loading more…" on every page. Asking two
  // screens early has the next page in place before they get there.
  const handleScroll = (e) => {
    if (disableAutoLoad) return;
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    const prefetchPx = Math.max(800, el.clientHeight * 2);
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < prefetchPx;
    if (nearBottom) onLoadMore();
  };
  const [widths, setWidths] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('pp_cols') || '');
      // A width saved before dates went four-digit-year may be too narrow
      // for them — lift it to the minimum.
      if (v && typeof v === 'object') return { ...COL_DEFAULTS, ...v, date: Math.max(Number(v.date) || COL_DEFAULTS.date, COL_MIN.date) };
    } catch {}
    return COL_DEFAULTS;
  });
  useEffect(() => { localStorage.setItem('pp_cols', JSON.stringify(widths)); }, [widths]);

  // Server-truth daily count comes from App.js; fall back to locally-known
  // messages if the endpoint hasn't responded yet.
  const localTodayCount = (() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const startMs = start.getTime();
    return messages.reduce((n, m) => n + (m.date && new Date(m.date).getTime() >= startMs ? 1 : 0), 0);
  })();
  const todayCount = typeof serverTodayCount === 'number' ? serverTodayCount : localTodayCount;

  // Filter + sort applied client-side over whatever's loaded.
  const mailboxLower = (mailbox || '').toLowerCase();
  // Memoised (Section 6 #2): the de-dupe + filter + sort over the whole loaded
  // collection only re-runs when its inputs change, not on every parent render.
  const visible = useMemo(() => {
    let list = messages;
    // De-dupe by id FIRST. Search merges local-FTS + Graph results and the
    // two can surface the SAME message, so `messages` can carry duplicate
    // ids. Each row is rendered with key={m.id}, so a duplicate id means two
    // rows share a React key — and on the next re-render (every search
    // keystroke refires the query) the reconciler throws "insertBefore … is
    // not a child of this node" and the list crashes. Keeping the first
    // occurrence is stable, cheap, and makes the keys unique by construction.
    if (Array.isArray(list)) {
      const seen = new Set();
      list = list.filter(m => {
        const id = m?.id;
        if (id == null) return true;      // id-less rows (shouldn't happen) pass through
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }
    if (labelFilter) list = list.filter(m => m.label === labelFilter);
    switch (filter) {
      case 'unread':    list = list.filter(m => !m.isRead); break;
      case 'flagged':   list = list.filter(m => m.flagged); break;
      case 'files':     list = list.filter(m => m.hasAttachments); break;
      case 'mentionsMe':
        if (mailboxLower) {
          list = list.filter(m =>
            (m.toAddresses || []).some(a => (a.email || '').toLowerCase() === mailboxLower)
          );
        }
        break;
      default: break;
    }
    const sign = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'from': {
          const av = String(a.fromName || a.from || '').toLowerCase();
          const bv = String(b.fromName || b.from || '').toLowerCase();
          return av.localeCompare(bv) * sign;
        }
        case 'subject': {
          const av = String(a.subject || '').toLowerCase().replace(/^(re:|fwd?:)\s*/i, '');
          const bv = String(b.subject || '').toLowerCase().replace(/^(re:|fwd?:)\s*/i, '');
          return av.localeCompare(bv) * sign;
        }
        default:
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * sign;
      }
    });
    return list;
  }, [messages, labelFilter, filter, mailboxLower, sortDir, sortBy]);

  // Arrow-key navigation through the visible list. Prevents the default
  // page-scroll behaviour and selects the next/previous row, scrolling
  // it into view. Skipped when focus is in an editable element so
  // typing in search / compose / rule inputs still works as expected.
  //
  // Listener is attached *once* — the handler reads the latest visible /
  // selectedId / onSelect via a ref. Without this, `visible` would be a
  // fresh array every render and the window listener would be removed +
  // re-added on every keystroke / polling tick, thrashing the event loop.
  const navRef = useRef({});
  navRef.current = { visible, selectedId, onSelect, onPrefetch };
  // When the previous arrow press was a moment ago the person is skimming, so
  // the email after the one they land on is fetched ahead (onPrefetch, the
  // same ?prefetch=1 GET as a mouse-down) and their next press shows it at
  // once. One press after reading for a while fetches nothing extra.
  const lastArrowAtRef = useRef(-Infinity);   // not 0: performance.now() starts at page load
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const t = e.target;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable) return;
      const { visible: vis, selectedId: selId, onSelect: sel, onPrefetch: prefetch } = navRef.current;
      if (!vis.length) return;
      const currentIdx = vis.findIndex(m => m.id === selId);
      const nextIdx = e.key === 'ArrowDown'
        ? (currentIdx < 0 ? 0 : Math.min(vis.length - 1, currentIdx + 1))
        : (currentIdx <= 0 ? 0 : currentIdx - 1);
      e.preventDefault();
      if (nextIdx === currentIdx) return;
      const nextMsg = vis[nextIdx];
      sel(nextMsg);
      const now = performance.now();
      const skimming = now - lastArrowAtRef.current < ARROW_SKIM_MS;
      lastArrowAtRef.current = now;
      const ahead = vis[nextIdx + (e.key === 'ArrowDown' ? 1 : -1)];
      if (skimming && ahead && prefetch) prefetch(ahead);
      requestAnimationFrame(() => {
        const el = listRef.current?.querySelector(`[data-msg-id="${CSS.escape(nextMsg.id)}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
      });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Mirror MessageRow's column template (with the new 18 px paperclip
  // column) so the column header row aligns perfectly with the data
  // rows below it.
  const gridTemplate = `14px 18px ${widths.from}px ${widths.reply}px 1fr ${widths.date}px 22px`;

  const makeDragHandler = (key, sign = 1) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    let lastX = e.clientX;
    const onMove = (ev) => {
      const delta = (ev.clientX - lastX) * sign;
      setWidths(prev => ({
        ...prev,
        [key]: Math.max(COL_MIN[key], Math.min(COL_MAX[key], prev[key] + delta)),
      }));
      lastX = ev.clientX;
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const resetCols = () => setWidths(COL_DEFAULTS);

  // Positions of the resize handles within the column header row.
  // ATTACH (18 px) was inserted between DOT and FROM, so the from-
  // right anchor needs to step over it; without this the resizer
  // floats 18 px to the left of the actual column boundary.
  const PAD_L = 10, GAP = 8, DOT = 14, ATTACH = 18, FLAG = 22, PAD_R = 12;
  const fromRight    = PAD_L + DOT + GAP + ATTACH + GAP + widths.from + GAP / 2;
  const dateLeftOff  = PAD_R + FLAG + GAP + widths.date + GAP / 2;

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          {folderName || 'Inbox'}
          <span className={styles.count}>
            {todayCount} {todayCount === 1 ? 'email' : 'emails'} today
          </span>
        </div>
        <div className={styles.actions}>
          {/* Sweep orphans — only renders when viewing the Drafts folder
              (App.js gates the prop). Click → opens the review modal
              that lists drafts paired with their matching sent emails;
              the user reviews and confirms before any deletion. The
              match rule is conservative (see graph.js findOrphanDrafts)
              so genuine work-in-progress drafts never show up here. */}
          {onSweepOrphanDrafts && (
            <button
              className={styles.dropdownBtn}
              onClick={(e) => { e.stopPropagation(); onSweepOrphanDrafts(); }}
              title="Find and review drafts whose corresponding email was already sent"
            >
              <Icon name="trashThin" size={11} />
              Sweep orphans
            </button>
          )}
          {onEmptyFolder && (
            <button
              className={`${styles.dropdownBtn} ${styles.dropdownBtnDanger}`}
              onClick={(e) => { e.stopPropagation(); onEmptyFolder(); }}
              title="Delete every message in this folder"
            >
              <Icon name="trashThin" size={11} />
              Empty Folder
            </button>
          )}
          {/* Delete the currently-selected message. Disabled when nothing is
              selected so the affordance stays visible (pill shows with
              muted colour) rather than popping in and out. Hidden on
              mobile — swipe-left on a row deletes there (see the
              swipeAction handlers in MessageRow). */}
          {onDelete && (
            <button
              className={`${styles.dropdownBtn} ${styles.dropdownBtnDanger} ${styles.desktopOnly}`}
              onClick={(e) => { e.stopPropagation(); if (selectedId) onDelete(selectedId); }}
              disabled={!selectedId}
              title={selectedId ? 'Delete the selected message' : 'Select a message to delete'}
            >
              <Icon name="trashThin" size={11} />
              Delete
            </button>
          )}
          <div className={styles.dropdownWrap}>
            <button
              className={styles.dropdownBtn}
              onClick={(e) => { e.stopPropagation(); setFilterOpen(o => !o); setSortOpen(false); }}
            >
              Filter: {FILTER_OPTS.find(o => o.key === filter)?.label || 'All'}
              <Icon name="chevronDown" size={11} />
            </button>
            {filterOpen && (
              <div className={styles.dropdownMenu} onClick={(e) => e.stopPropagation()}>
                {FILTER_OPTS.map(o => (
                  <button
                    key={o.key}
                    className={filter === o.key ? styles.dropdownActive : ''}
                    onClick={() => { onFilterChange(o.key); setFilterOpen(false); }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.dropdownWrap}>
            <button
              className={styles.dropdownBtn}
              onClick={(e) => { e.stopPropagation(); setSortOpen(o => !o); setFilterOpen(false); }}
            >
              Sort: {SORT_FIELDS.find(f => f.key === sortBy)?.label} · {sortDir === 'asc' ? 'Oldest first' : 'Newest first'}
              <Icon name="chevronDown" size={11} />
            </button>
            {sortOpen && (
              <div className={styles.dropdownMenu} onClick={(e) => e.stopPropagation()}>
                {SORT_FIELDS.map(f => (
                  <button
                    key={f.key}
                    className={sortBy === f.key ? styles.dropdownActive : ''}
                    onClick={() => setSortBy(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
                <div className={styles.dropdownSep} />
                <button
                  className={sortDir === 'desc' ? styles.dropdownActive : ''}
                  onClick={() => setSortDir('desc')}
                >
                  Newest first
                </button>
                <button
                  className={sortDir === 'asc' ? styles.dropdownActive : ''}
                  onClick={() => setSortDir('asc')}
                >
                  Oldest first
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.columns} style={{ gridTemplateColumns: gridTemplate }}>
        <span />
        {/* Paperclip column header — empty span keeps the alignment
            without a label (the icon-only column doesn't need one). */}
        <span />
        <span className={styles.colLabel}>{showTo ? 'To' : 'From'}</span>
        <span />
        <span className={styles.colLabel}>Subject</span>
        <span className={`${styles.colLabel} ${styles.colLabelRight}`}>Date</span>
        <span />
        {/* Absolutely-positioned resize handles */}
        <span
          className={styles.colResizer}
          style={{ left: `${fromRight}px` }}
          onMouseDown={makeDragHandler('from')}
        />
        <span
          className={styles.colResizer}
          style={{ right: `${dateLeftOff}px` }}
          onMouseDown={makeDragHandler('date', -1)}
        />
      </div>

      {/* Optional caller-supplied banner — rendered between the column
          headers and the first row. Used by App.js to surface thread-
          mode context ("Thread · domain · N emails · Clear"). Sits
          outside the scrollable list so it stays pinned. */}
      {headerSlot}

      <div className={styles.list} ref={listRef} onScroll={handleScroll}>
        {loading && !visible.length && (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            Loading emails…
          </div>
        )}
        {!loading && !visible.length && (
          <div className={styles.empty}>No messages</div>
        )}
        {(() => {
          // Outlook-style date buckets — render a sticky header when the
          // bucket changes between consecutive rows. Bucket order: Today,
          // Yesterday, Last week, Last month, then per-month/year.
          const out = [];
          let lastBucket = null;
          const bucketLabelForDate = makeBucketLabeler();
          // Row dates are relative to today (HH:mm vs DD/MM/YY), so a new day
          // must reach every memoised row once — this prop changes at midnight.
          const dayKey = new Date().toDateString();
          // Day groups only make sense in date order; other sorts keep the
          // per-row windowing.
          const byDay = RENDER_CV && sortBy === 'date';
          let group = null;   // { key, rows } — the day being collected
          const usedKeys = new Set();
          const flush = () => {
            if (!group) return;
            out.push(<div key={group.key} style={cvDayStyle(group.rows.length)}>{group.rows}</div>);
            group = null;
          };
          for (const m of visible) {
            const bucket = bucketLabelForDate(m.date);
            if (bucket !== lastBucket) {
              flush();
              // /V2 skips the "Today" heading — today's rows are the top of
              // the list anyway; headings mark the earlier days as you scroll.
              if (!(V2 && bucket === 'Today')) {
                out.push(
                  <div key={`bucket-${bucket}-${m.id}`} className={styles.bucketHeader}>
                    {bucket}
                  </div>
                );
              }
              lastBucket = bucket;
            }
            const row = (
              <MessageRow
                key={m.id}
                msg={m}
                dayKey={dayKey}
                ownCv={RENDER_CV && !byDay}
                active={m.id === selectedId}
                onClick={rowOnSelect}
                onContext={rowOnContext}
                onFlag={rowOnFlag}
                onPrefetch={rowOnPrefetch}
                widths={widths}
                showTo={showTo}
                mailbox={mailbox}
                mixedFolders={mixedFolders}
                onSwipeUnread={rowOnSwipeUnread}
                onSwipeDelete={rowOnSwipeDelete}
                buildOrderActive={buildOrderActive}
                tickedEntry={buildOrderTickedEmails[m.id] || null}
                onToggleBuildOrderTick={rowOnToggleBuildOrderTick}
              />
            );
            if (!byDay) { out.push(row); continue; }
            const day = localDayKey(m.date);
            if (!group || group.day !== day) {
              flush();
              let key = `day-${day}`;
              for (let n = 2; usedKeys.has(key); n++) key = `day-${day}-${n}`;   // undated rows can recur
              usedKeys.add(key);
              group = { key, day, rows: [] };
            }
            group.rows.push(row);
          }
          flush();
          return out;
        })()}
        {!loading && hasMore && (
          <div className={styles.loadMore}>
            {loadingMore
              ? <><span className={styles.spinner} /> Loading more…</>
              : <button className={styles.loadMoreBtn} onClick={onLoadMore}>Load more messages</button>}
          </div>
        )}
        {!loading && onSearchOlder && (
          <div className={styles.loadMore}>
            {searchingOlder
              ? <><span className={styles.spinner} /> Searching older mail…</>
              : <button className={styles.loadMoreBtn} onClick={onSearchOlder}>
                  Search full mailbox (older than {LOCAL_INDEX_DAYS} days)
                </button>}
          </div>
        )}
        {!loading && messages.length > 0 && !hasMore && !onSearchOlder && (
          <div className={styles.endMark}>— end of folder —</div>
        )}
      </div>
    </section>
  );
}
